import { Divider, Grid, InputAdornment } from '@material-ui/core';
import { styled } from '@material-ui/core/styles';
import MuiToggleButton from '@material-ui/lab/ToggleButton';
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup';
import {
  AutoComplete,
  Dialog,
  Icons,
  Select,
  TextField,
  Typography,
} from '@nutrien/cxp-components';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useMemo, useState } from 'react';

import { assignInspector } from '../../graphql';
import { useMst } from '../../mobx-models/Root';
import { IFieldInspectorList } from '../../models/models';
import useSelectedTask from '../../rxdb/taskList/useSelectedTask';
import { useRxDB } from '../../rxdb/useRxDB';
import { isJestTest } from '../../test-helpers/isJestTest';
import { mockFieldInspectorList, mockSPList } from '../../test-helpers/mockData/assignInspector';
import { mockFIList } from '../../test-helpers/mockData/viewInspectors';
import { useNotification } from '../../utilities/useNotification';
import { FieldInspectorType } from '../../utilities/enums';
import { isNull } from '../../utilities/utilityFunctions';
import CustomButton from '../CustomButton/CustomButton';
import StyledButton from '../InspectorInfo/StyledButton';
import useAddInspectorStyles from './AddInspectors.styles';
import useReAssignStyles from './ReAssignInspector.styles';

const ToggleButton = styled(MuiToggleButton)(({}) => ({
  '&.Mui-selected:not(.Mui-disabled), &.Mui-selected:hover:not(.Mui-disabled)': {
    color: '#121212',
    backgroundColor: 'rgba(42, 171, 63, 1)',
  },
  '&.Mui-selected, &.Mui-selected:hover': {
    color: '#121212',
    backgroundColor: 'dimgray',
  },
}));

interface Props {
  open: boolean;
  onClose: () => void;
  taskId: number;
}

const AdhocInspectionAssignment: React.FC<Props> = ({ open, onClose, taskId }: Props) => {
  const reAssignClasses = useReAssignStyles();
  const addInspectorClasses = useAddInspectorStyles();
  const { primaryContext, CurrentUserSelected, SiteDiscipline } = useMst();
  const { db } = useRxDB();
  const { taskData, isTaskDetailLoading } = useSelectedTask(taskId);
  const { successNotification, errorNotification } = useNotification();
  const loggedInUser = CurrentUserSelected?.currentUser;
  const [selectedInspector, setSelectedInspector] = useState<{
    fieldInspectorId?: number;
    fieldInspectorName?: string;
    clerkId?: number;
    clerkName?: string;
  }>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [description, setDescription] = useState('');
  const [inspectorType, setInspectorType] = useState<string>('Nutrien');
  const [serviceProviderId, setServiceProviderId] = useState<number>(1);
  const [isDECView, setIsDECView] = useState(false);
  const [disableToggleButton] = useState(false);
  const [fieldInspectorType, setFieldInspectorType] = useState<string>(FieldInspectorType.FI);

  const allFieldInspectorLIst = isJestTest()
    ? mockFieldInspectorList
    : isDECView
    ? primaryContext.dataEntryClerksList.toJSON()
    : primaryContext.fieldInspectorList.toJSON();

  const allLeadInspectorList = isJestTest()
    ? mockFieldInspectorList
    : primaryContext.fieldInspectorList.toJSON();

  const assignedLeadInspector = [...allLeadInspectorList]?.find(
    e => e.pkInspector === taskData?.fkInspectorLead,
  );
  const assignedFieldInspector = [...allFieldInspectorLIst]?.find(
    e => e.pkInspector === taskData?.fkInspectorField || e.clerkId === taskData?.clerkId,
  );

  const handleMainToggleChange = (
    _event: React.MouseEvent<HTMLElement, MouseEvent>,
    inspType: string,
  ) => {
    if (inspType !== null) {
      setFieldInspectorType(inspType);
    }
  };

  const onCommentsInput = (event: React.FocusEvent<HTMLInputElement>) => {
    const {
      target: { value },
    } = event;
    setDescription(value);
  };

  const onAssign = async () => {
    setIsLoading(true);
    const isReassignmentByFI = taskData?.fkInspectorLead !== loggedInUser?.inspectorId;
    try {
      const result = await assignInspector(
        taskData?.pkAssetInspectionTask,
        taskData?.fkSchedule,
        taskData?.fkAssetInspection,
        taskData?.fkInspectorLead,
        Number(selectedInspector?.fieldInspectorId || 0),
        description,
        taskData?.isReviewedByLI,
        taskData?.isReviewedByFI,
        taskData?.commentInspectorLead,
        taskData?.commentInspectorField,
        true,
        taskData?.proxyInspector || '',
        Number(selectedInspector?.clerkId || 0),
        taskData?.site,
        taskData?.discipline,
        taskData?.pkUuId,
        5,
        !!CurrentUserSelected?.currentUser?.clerkId,
        isNull(taskData, 'recommendations', ''),
        isNull(taskData, 'dateCompleted', null),
        isReassignmentByFI,
      );
      if (!result?.errors) {
        successNotification(
          `${!isDECView ? 'Field Inspector' : 'Data Entry Clerk'} re-assigned Successfully`,
        );
        setIsLoading(false);
        db?.startReplication();
        const siteId = SiteDiscipline.siteId;
        const disciplineId = SiteDiscipline.disciplineId;
        primaryContext.getwpFilesFromMobx(siteId, disciplineId);
        primaryContext.getWorkPackageFromMobx(siteId, disciplineId);
        primaryContext.getInspectionListFromMobx(siteId, disciplineId);
        onClose();
      }
    } catch (error) {
      errorNotification(
        `${!isDECView ? 'Field Inspector' : 'Data Entry Clerk'} is not updated please try again`,
      );
      setIsLoading(false);
    }
  };

  const handleSearch = (
    event: React.KeyboardEvent<HTMLDivElement>,
    inspector: IFieldInspectorList,
  ) => {
    if (!inspector) return;
    if (
      event.key === 'Enter' &&
      (selectedInspector?.fieldInspectorId || selectedInspector?.clerkId)
    ) {
      onAssign();
      return;
    }
    const name =
      inspector?.pkInspector === loggedInUser?.inspectorId
        ? loggedInUser?.name
        : `${inspector?.firstName} ${inspector?.lastName}`;

    setSelectedInspector({
      fieldInspectorId: inspector?.pkInspector || 0,
      fieldInspectorName: name,
      clerkId: inspector?.clerkId || 0,
      clerkName: inspector?.clerkName,
    });
  };

  const handleOnSubmit = (event: React.KeyboardEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (selectedInspector?.fieldInspectorId) {
      onAssign();
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && selectedInspector?.fieldInspectorId) {
      onAssign();
    }
  };

  const handleToggleChange = (
    event: React.MouseEvent<HTMLElement, MouseEvent>,
    inspType: string,
  ) => {
    if (inspType !== null) {
      if (inspType === 'Nutrien') {
        setServiceProviderId(1);
      }
      setInspectorType(inspType);
    }
  };

  const selectedFilteredInspector = useMemo(() => {
    if (isDECView) {
      return selectedInspector?.clerkId ? selectedInspector : {};
    } else if (selectedInspector?.fieldInspectorId) {
      return selectedInspector;
    }
    return {};
  }, [selectedInspector, isDECView]);

  const inspectorList = useMemo(() => {
    if (allFieldInspectorLIst?.length) {
      return [...allFieldInspectorLIst]
        ?.filter(inspector => {
          if (inspector?.fkServiceProvider !== serviceProviderId || !inspector?.isActive) {
            return false;
          }

          if (selectedFilteredInspector && Object.keys(selectedFilteredInspector).length === 0) {
            return true;
          }

          if (!isDECView) {
            return (
              selectedInspector?.fieldInspectorId &&
              selectedInspector?.fieldInspectorId != inspector?.pkInspector
            );
          }

          return selectedInspector?.clerkId != inspector?.clerkId;
        })
        ?.sort((a, b) =>
          a.pkInspector == loggedInUser?.inspectorId
            ? -1
            : b.pkInspector == loggedInUser?.inspectorId
            ? 1
            : 0,
        )
        .map(inspector => {
          if (inspector.pkInspector === loggedInUser?.inspectorId) {
            return { ...inspector, name: 'Assigned To Me' };
          }
          return { ...inspector, name: `${inspector.firstName} ${inspector.lastName}` };
        });
    }

    return [];
  }, [serviceProviderId, allFieldInspectorLIst, selectedInspector]);

  useEffect(() => {
    let isMounted = true;
    const getMasterData = async () => {
      try {
        if (isMounted) {
          await Promise.all([
            primaryContext.getFIListFromMobx(),
            primaryContext.getSPListFromMobx(),
            primaryContext.getDataEntryClerksFromMobx(),
          ]);
        }
      } catch (e) {
        if (isMounted) {
          errorNotification('data is not loaded');
        }
      }
    };
    getMasterData();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!isTaskDetailLoading) setDescription(taskData?.taskDescription);
  }, [isTaskDetailLoading]);

  useEffect(() => {
    setSelectedInspector({
      fieldInspectorId: assignedFieldInspector?.pkInspector,
      fieldInspectorName: `${assignedFieldInspector?.firstName} ${assignedFieldInspector?.lastName}`,
      clerkId: assignedFieldInspector?.clerkId,
      clerkName: assignedFieldInspector?.clerkName,
    });
  }, [assignedFieldInspector]);

  useEffect(() => {
    const isDECViewSelected = fieldInspectorType === FieldInspectorType.DEC;
    setIsDECView(isDECViewSelected);
  }, [fieldInspectorType, assignedFieldInspector]);

  const getDivider = () => {
    return <Divider orientation="horizontal" className={reAssignClasses.divider} />;
  };

  return (
    <>
      <Dialog
        open={open}
        titleText={`Re-Assign ${!isDECView ? 'Inspector' : 'Data Entry Clerk'}`}
        onClose={onClose}
        backButton={false}
        dialogActions={
          <CustomButton
            isLoading={isLoading}
            onClose={onAssign}
            disabled={
              (assignedFieldInspector?.pkInspector &&
                selectedInspector?.fieldInspectorId === assignedFieldInspector?.pkInspector) ||
              (assignedFieldInspector?.clerkId &&
                selectedInspector?.clerkId === assignedFieldInspector?.clerkId)
            }
            buttonText="Re-Assign"
            dataTestId="Re-Assign-Adhoc"
          />
        }
        maxWidth="xs"
        noMinHeight
      >
        <form data-testid="adhoc-form" onSubmit={handleOnSubmit}>
          <Grid container className={reAssignClasses.root}>
            <ToggleButtonGroup
              color="#121212"
              value={fieldInspectorType}
              exclusive
              onChange={handleMainToggleChange}
            >
              <ToggleButton
                disabled={disableToggleButton}
                value={FieldInspectorType.FI}
                data-testid="nutrientogglebutton"
              >
                Field Inspector
              </ToggleButton>
              <ToggleButton
                disabled={disableToggleButton}
                value={FieldInspectorType.DEC}
                data-testid="sptogglebutton"
              >
                Data Entry Clerk
              </ToggleButton>
            </ToggleButtonGroup>
            {getDivider()}
            {assignedLeadInspector && (
              <>
                <Grid item container xs={12} alignItems="center">
                  <Grid item container alignItems="center">
                    <Typography variant="body2" className={reAssignClasses.nameLabel}>
                      Lead Inspector
                    </Typography>
                  </Grid>
                  <Grid item container>
                    <Typography variant="body1" className={reAssignClasses.leadInspectorName}>
                      {`${assignedLeadInspector.firstName} ${assignedLeadInspector.lastName}`}
                    </Typography>
                  </Grid>
                </Grid>
                {getDivider()}
              </>
            )}

            {selectedFilteredInspector && Object.keys(selectedFilteredInspector).length > 0 && (
              <>
                {' '}
                <Grid item container xs={12} alignItems="center">
                  <Grid item container alignItems="center">
                    <Typography variant="body2" className={reAssignClasses.nameLabel}>
                      Assigned {!isDECView ? 'Inspector' : 'Data Entry Clerk'}
                    </Typography>
                  </Grid>
                  <Grid item container>
                    <StyledButton inspector={selectedFilteredInspector} canDelete={false} />
                  </Grid>
                </Grid>
                {getDivider()}
              </>
            )}

            <Grid item xs={12}>
              <ToggleButtonGroup
                color="#121212"
                value={inspectorType}
                exclusive
                onChange={handleToggleChange}
              >
                <ToggleButton value="Nutrien">Nutrien</ToggleButton>
                <ToggleButton value="ServiceProvider">Service Provider</ToggleButton>
              </ToggleButtonGroup>

              {inspectorType === 'ServiceProvider' && (
                <Grid className={addInspectorClasses.spSelectGrid} item xs={12}>
                  <Select
                    className={addInspectorClasses.spSelect}
                    label="Service Provider"
                    list={
                      isJestTest()
                        ? mockSPList
                        : primaryContext.serviceProviderList.filter(e => e.pkServiceProvider !== 1)
                    }
                    getOptionLabel={list => `${list.organizationName}`}
                    getOptionValue={list => list.pkServiceProvider}
                    onChange={event => setServiceProviderId(event.target.value)}
                    defaultValue={serviceProviderId}
                    noMinHeight
                    data-testid="splistoptions"
                  />
                </Grid>
              )}

              <Grid item className={addInspectorClasses.autoCompleteGrid}>
                <AutoComplete
                  autoSelect={false}
                  key="field-inspector"
                  noMinHeight
                  size="small"
                  label={`${!isDECView ? 'Field Inspector' : 'Data Entry Clerk'}`}
                  closeIcon=""
                  startAdornment={
                    <InputAdornment position="start">
                      <Icons.SearchFeather color="rgba(149, 149, 149, 1)" height={20} width={20} />
                    </InputAdornment>
                  }
                  endAdornment={
                    <InputAdornment position="end">
                      <Icons.PlusCircleFeather
                        className={addInspectorClasses.plusIcon}
                        color="primary"
                        strokeWidth="1.5px"
                      />
                    </InputAdornment>
                  }
                  placeholder={`Search ${!isDECView ? 'Inspector' : 'Data Entry Clerk'}`}
                  onChange={handleSearch}
                  list={isJestTest() ? mockFIList : inspectorList}
                  value=""
                  getOptionLabel={option => (option ? option.name : option)}
                />
              </Grid>
            </Grid>

            <Grid item container className={reAssignClasses.commentPadding}>
              <Typography variant="subtitle2" className="reassignInspectorLabel">
                Comments
              </Typography>
              <TextField
                defaultValue={description}
                onChange={onCommentsInput}
                onKeyDown={handleKeyDown}
                noMinHeight
                fullWidth
                variant="outlined"
                size="small"
                placeholder="Type"
                multiline
                rows={3}
              />
            </Grid>
          </Grid>
        </form>
      </Dialog>
    </>
  );
};

export default observer(AdhocInspectionAssignment);
